/**
 * Pick which words to show pronunciation for (text-only, no audio scores).
 *
 * Pipeline:
 * 1) normalize tokens
 * 2) remove stopwords + numbers + length<=2
 * 3) deduplicate (keep first occurrence)
 * 4) score by "hard patterns"
 * 5) sort by score desc, then length desc
 * 6) take top N
 *
 * Returns: [{ word, score, reasons }]
 */

export function pickWords(inputWords) {
    const options = {
        minLen: 3,
        maxLen: 28,
        allowShortTricky: true,
    };

    const STOPWORDS = getStopwordsSet();

    // 1) Normalize tokens
    const cleaned = [];
    for (const raw of inputWords || []) {
        const w = normalizeWord(raw);
        if (!w) continue;
        if (w.length > options.maxLen) continue;
        cleaned.push(w);
    }

    const filtered = [];
    for (const w of cleaned) {
        if (STOPWORDS.has(w)) continue;
        if (isNumberToken(w)) continue;

        if (w.length < options.minLen) continue;

        if (!/[a-z]/.test(w)) continue;

        filtered.push(w);
    }

    const seen = new Set();
    const unique = [];
    for (const w of filtered) {
        if (seen.has(w)) continue;
        seen.add(w);
        unique.push(w);
    }

    console.log(unique);

    return unique;
}

export function sortTheWords(unique) {

    // 4) Score using hard patterns
    const scored = unique
        .map((w) => scoreHardness(w, { allowShortTricky: true}))
        // Optional: remove words that are not hard at all if you want extra minimal UI
        // .filter(x => x.score > 0)
        ;

    // 5) Sort score desc, then length desc, then original (stable-ish)
    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (b.word.length !== a.word.length) return b.word.length - a.word.length;
        return a.word.localeCompare(b.word);
    });

    // 6) Take top N
    return scored ; 
}

/* ---------------------- Helpers ---------------------- */

function normalizeWord(raw) {
    if (raw == null) return "";
    let s = String(raw).toLowerCase().trim();

    // Strip leading/trailing punctuation (keep internal apostrophes/hyphens)
    s = s.replace(/^[^a-z0-9']+|[^a-z0-9']+$/g, "");

    // Common cleanup: smart apostrophes
    s = s.replace(/[’]/g, "'");

    // If token became empty
    return s;
}

function isNumberToken(w) {
    // Pure digits or digits with decimals/commas/percent/ordinals
    // Examples: 12, 2.5, 10%, 1,000, 5th
    return /^(\d+([.,]\d+)?%?|\d{1,3}(,\d{3})+%?|\d+(st|nd|rd|th))$/.test(w);
}

/**
 * Hard-pattern scoring:
 * - Uses a mix of:
 *   A) known tricky word list (highest priority)
 *   B) rare letter patterns
 *   C) suffix rules that often change pronunciation
 *   D) vowel complexity / ambiguity
 *
 * Output includes "reasons" so you can show tooltips like:
 *  queue (tricky spelling), pronunciation (many vowels)
 */
function scoreHardness(word, { allowShortTricky } = {}) {
    const reasons = [];
    let score = 0;

    // A) Known tricky words (high ROI)
    // Keep small, you can expand over time.
    const TRICKY_WORDS = getTrickyWordsSet();
    if (TRICKY_WORDS.has(word)) {
        score += 6;
        reasons.push("common tricky word");
    }

    // If very short, we usually skip — but if allowShortTricky and it's in tricky list, keep it.
    if (word.length <= 3 && !allowShortTricky && !TRICKY_WORDS.has(word)) {
        return { word, score: 0, reasons: [] };
    }

    // B) Starts-with silent / uncommon clusters
    if (/^(kn|gn|wr|ps)/.test(word)) {
        score += 4;
        reasons.push("silent-letter start (kn/gn/wr/ps)");
    }

    // C) High-confusion chunks
    const chunkRules = [
        { re: /ough/, pts: 4, why: "ough has many sounds" },
        { re: /tion|sion|cian/, pts: 3, why: "tion/sion often becomes 'shun'" },
        { re: /ph/, pts: 2, why: "ph often sounds like 'f'" },
        { re: /qu/, pts: 2, why: "qu can be tricky" },
        { re: /ch/, pts: 1, why: "ch varies (ch/k/sh)" },
        { re: /eau/, pts: 3, why: "eau is uncommon vowel pattern" },
        { re: /igh|ght/, pts: 2, why: "igh/ght patterns vary" },
        { re: /mb\b/, pts: 3, why: "mb ending often has silent b" },
        { re: /bt\b|pt\b|mn\b/, pts: 2, why: "silent consonant endings" },
        { re: /sch/, pts: 2, why: "sch pattern varies" },
        { re: /x/, pts: 1, why: "x has multiple sounds" },
    ];

    for (const rule of chunkRules) {
        if (rule.re.test(word)) {
            score += rule.pts;
            reasons.push(rule.why);
        }
    }

    // D) Vowel complexity
    const vowelCount = (word.match(/[aeiouy]/g) || []).length;
    const vowelRuns = (word.match(/[aeiouy]{2,}/g) || []).length; // consecutive vowels
    if (vowelRuns >= 1) {
        score += Math.min(3, vowelRuns); // cap
        reasons.push("has consecutive vowels");
    }
    if (vowelCount >= 4) {
        score += 1;
        reasons.push("many vowels");
    }

    // E) R-controlled (hard for many learners)
    if (/(er|ir|ur)/.test(word)) {
        score += 1;
        reasons.push("r-controlled vowel (er/ir/ur)");
    }

    // F) Suffixes that change pronunciation
    // -ed / -es / -s often require rules (t/d/ɪd, s/z/ɪz)
    if (/(ed|es|s)\b/.test(word)) {
        // avoid simple plurals like "cars" getting too much weight
        const pts = word.endsWith("ed") ? 2 : word.endsWith("es") ? 2 : 1;
        score += pts;
        reasons.push("ending changes sound (-s/-es/-ed)");
    }

    // G) Length bonus (longer words are harder to decode)
    if (word.length >= 8) score += 1;
    if (word.length >= 12) score += 1;

    // H) Extra: doubled consonants can confuse (e.g., "accommodate")
    if (/(bb|cc|dd|ff|gg|ll|mm|nn|pp|rr|ss|tt)/.test(word)) {
        score += 1;
        reasons.push("double consonants");
    }

    // De-duplicate reasons (some rules overlap)
    const uniqReasons = [...new Set(reasons)];

    return { word, score, reasons: uniqReasons };
}

/* -------- Word lists (you can expand anytime) -------- */

function getStopwordsSet() {
    // Common English stopwords (small-ish list; add more as needed)
    return new Set([
        "a", "an", "the", "and", "or", "but", "so", "because", "if", "then", "than",
        "to", "of", "in", "on", "at", "for", "from", "with", "without", "as", "by", "into", "onto", "over", "under",
        "is", "am", "are", "was", "were", "be", "been", "being",
        "do", "does", "did", "done", "doing",
        "have", "has", "had", "having",
        "i", "me", "my", "mine", "we", "us", "our", "ours",
        "you", "your", "yours",
        "he", "him", "his", "she", "her", "hers", "they", "them", "their", "theirs",
        "it", "its", "this", "that", "these", "those",
        "there", "here", "who", "whom", "which", "what", "when", "where", "why", "how",
        "not", "no", "yes",
        "very", "just", "also", "too", "really", "only", "even",
        "uh", "um", "like"
    ]);
}

function getTrickyWordsSet() {
    // High ROI tricky words; add based on your user base and curriculum
    return new Set([
        "queue", "colonel", "debt", "knife", "knight", "psychology", "receipt", "island",
        "honest", "hour", "choir", "yacht", "squirrel", "though", "through", "rough", "cough",
        "read", "lead", "tear", "wind", "minute", "present", "record", "object", "permit",
        "schedule", "vehicle", "comfortable", "vegetable", "clothes", "Wednesday".toLowerCase()
    ]);
}
